// src/TX3CardManager.js
// Cardholders tab of Badge Management. Adds/edits/deletes Mircom TX3 access
// cards on the panel over USB-serial, through the serial service that owns COM3
// (always on with the server). Mirrors BadgeManagement's patterns: axiosInstance
// (baseURL '/api'), ProtectedComponent gating, and a matching dark theme.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import ScanHistoryModal from './ScanHistoryModal';
import './styles/TX3CardManager.css';

function formatTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const EMPTY_ADD = { name: '', cardNumber: '', accessLevelId: '', facilityCode: '' };

// Card status: 1 = active/enabled, 0 = disabled (a deleted card is zeroed).
function statusLabel(s) {
  if (s === 1) return 'Active';
  if (s === 0) return 'Disabled';
  return s == null ? '—' : String(s);
}


function TX3CardManager() {
  // Serial service
  const [serial, setSerial] = useState({
    running: false, port: '', swipes: 0, writes: 0, polls: 0,
    lastSwipeAt: null, startedAt: null, lastError: null,
  });
  const [serialBusy, setSerialBusy] = useState(false);
  const [serialMsg, setSerialMsg] = useState(null); // { type, text } — start/stop result

  // Cards + reference data
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add form
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [adding, setAdding] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', cardNumber: '', accessLevelId: '', facilityCode: '' });
  const [rowBusy, setRowBusy] = useState(null); // CardID currently saving/deleting

  // Scan-history modal (opened by clicking a cardholder name)
  const [histCard, setHistCard] = useState(null); // the card whose history is shown

  // Toast for add/edit/delete outcomes
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', text }
  const toastTimer = useRef(null);

  const showToast = useCallback((type, text) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ type, text });
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/tx3-cards/serial/status');
      setSerial((prev) => ({ ...prev, ...data }));
    } catch (err) {
      // Non-fatal: leave the last known status, note the error on the bar.
      setSerial((prev) => ({ ...prev, lastError: err.response?.data?.message || 'Status unavailable' }));
    }
  }, []);

  const fetchCards = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/tx3-cards/cards');
      setCards(data.cards || []);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load cards.');
      return false;
    }
  }, []);


  // Initial load
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      await Promise.all([fetchStatus(), fetchCards()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [fetchStatus, fetchCards]);

  // Light auto-refresh of the serial status so the counters stay live.
  useEffect(() => {
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // Clear the toast timer on unmount.
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // ── serial service control ──
  const startSerial = async () => {
    setSerialBusy(true);
    setSerialMsg(null);
    try {
      const { data } = await axiosInstance.post('/tx3-cards/serial/start');
      setSerialMsg({ type: 'success', text: data.message || `Serial service started on ${data.port || serial.port || 'COM3'}.` });
      await fetchStatus();
    } catch (err) {
      // COM3 busy (Configurator connected) returns 400 with a message — surface it.
      setSerialMsg({ type: 'error', text: err.response?.data?.message || 'Failed to start the serial service.' });
      await fetchStatus();
    } finally {
      setSerialBusy(false);
    }
  };

  const stopSerial = async () => {
    setSerialBusy(true);
    setSerialMsg(null);
    try {
      const { data } = await axiosInstance.post('/tx3-cards/serial/stop');
      setSerialMsg({ type: 'success', text: data.message || 'Serial service stopped.' });
      await fetchStatus();
    } catch (err) {
      setSerialMsg({ type: 'error', text: err.response?.data?.message || 'Failed to stop the serial service.' });
      await fetchStatus();
    } finally {
      setSerialBusy(false);
    }
  };

  // ── add ──
  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || addForm.cardNumber === '') {
      showToast('error', 'Name and card number are required.');
      return;
    }
    setAdding(true);
    try {
      const body = {
        name: addForm.name.trim(),
        facilityCode: addForm.facilityCode === '' ? 0 : Number(addForm.facilityCode),
        cardNumber: Number(addForm.cardNumber),
        accessLevelId: addForm.accessLevelId === '' ? null : Number(addForm.accessLevelId),
      };
      const { data } = await axiosInstance.post('/tx3-cards/cards', body);
      const confirmed = data && data.accepted === false ? ' (panel did not confirm)' : '';
      showToast('success', (data && data.message) || `Card "${body.name}" added.${confirmed}`);
      setAddForm(EMPTY_ADD);
      await fetchCards();
      await fetchStatus();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to add card.');
    } finally {
      setAdding(false);
    }
  };

  // ── edit (inline) ──
  const beginEdit = (card) => {
    setEditingId(card.CardID);
    setEditForm({
      name: card.CardName ?? '',
      cardNumber: card.CardNumber ?? '',
      // The card list does not return an access level id, so we cannot
      // pre-select it — the editor starts unset.
      accessLevelId: '',
      facilityCode: card.FacilityCode ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', cardNumber: '', accessLevelId: '', facilityCode: '' });
  };

  const saveEdit = async (cardId) => {
    if (!editForm.name.trim() || editForm.cardNumber === '') {
      showToast('error', 'Name and card number are required.');
      return;
    }
    setRowBusy(cardId);
    try {
      const body = {
        name: editForm.name.trim(),
        facilityCode: editForm.facilityCode === '' ? 0 : Number(editForm.facilityCode),
        cardNumber: Number(editForm.cardNumber),
        accessLevelId: editForm.accessLevelId === '' ? null : Number(editForm.accessLevelId),
      };
      const { data } = await axiosInstance.put(`/tx3-cards/cards/${cardId}`, body);
      const confirmed = data && data.accepted === false ? ' (panel did not confirm)' : '';
      showToast('success', (data && data.message) || `Card "${body.name}" updated.${confirmed}`);
      cancelEdit();
      await fetchCards();
      await fetchStatus();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to update card.');
    } finally {
      setRowBusy(null);
    }
  };

  // ── delete ──
  const deleteCard = async (card) => {
    const label = card.CardName || `card #${card.CardNumber}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${label}? This removes it from the TX3 panel.`)) return;
    setRowBusy(card.CardID);
    try {
      const { data } = await axiosInstance.delete(`/tx3-cards/cards/${card.CardID}`);
      const confirmed = data && data.accepted === false ? ' (panel did not confirm)' : '';
      showToast('success', (data && data.message) || `Deleted ${label}.${confirmed}`);
      await fetchCards();
      await fetchStatus();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to delete card.');
    } finally {
      setRowBusy(null);
    }
  };

  const running = !!serial.running;
  const port = serial.port || 'COM3';

  return (
    <ProtectedComponent functionCode="OP14" functionName="Badge Management">
      <div className="tx3cm">
        <div className="tx3cm-header">
          <h1>TX3 Access Cards</h1>
          <div className="tx3cm-subrow">
            <p className="tx3cm-sub">Add, edit, and remove cards on the Mircom TX3 panel.</p>
            <span className={`tx3cm-pill ${running ? 'running' : 'stopped'}`}>
              {running ? 'TX3 ONLINE' : 'TX3 OFFLINE'}
            </span>
          </div>
        </div>

        {(serialMsg || serial.lastError) && (
          <div className="tx3cm-serial-msgs">
            {serialMsg && (
              <div className={`tx3cm-inline ${serialMsg.type}`}>{serialMsg.text}</div>
            )}
            {serial.lastError && !serialMsg && (
              <div className="tx3cm-inline error">Last error: {serial.lastError}</div>
            )}
          </div>
        )}

        {/* Add card form (dialog layout: labels left, inputs right) */}
        <section className="tx3cm-add">
          <h2>Add card</h2>
          <form onSubmit={submitAdd}>
            <div className="tx3cm-field">
              <label htmlFor="tx3cm-add-name">Name</label>
              <input
                id="tx3cm-add-name"
                type="text"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Cardholder name"
              />
            </div>
            <div className="tx3cm-field">
              <label htmlFor="tx3cm-add-number">Card number</label>
              <input
                id="tx3cm-add-number"
                type="number"
                value={addForm.cardNumber}
                onChange={(e) => setAddForm((f) => ({ ...f, cardNumber: e.target.value }))}
                placeholder="e.g. 12345"
              />
            </div>
            <div className="tx3cm-field">
              <label htmlFor="tx3cm-add-fc">Facility code</label>
              <input
                id="tx3cm-add-fc"
                type="number"
                value={addForm.facilityCode}
                onChange={(e) => setAddForm((f) => ({ ...f, facilityCode: e.target.value }))}
                placeholder="e.g. 0"
              />
            </div>
            <div className="tx3cm-add-footer">
              <button type="submit" className="tx3cm-btn primary" disabled={adding || !running}>
                {adding ? 'Adding…' : 'Add'}
              </button>
              {!running && (
                <span className="tx3cm-hint">Start the serial service to enable card writes.</span>
              )}
            </div>
          </form>
        </section>

        {/* Action toast */}
        {toast && <div className={`tx3cm-toast ${toast.type}`}>{toast.text}</div>}

        {/* Card list */}
        <section className="tx3cm-list">
          <h2>Cards {!loading && !error ? <span className="tx3cm-count">({cards.length})</span> : null}</h2>

          {error ? (
            <div className="tx3cm-inline error">{error}</div>
          ) : loading ? (
            <div className="tx3cm-loading">Loading cards…</div>
          ) : cards.length === 0 ? (
            <p className="tx3cm-empty">No cards found.</p>
          ) : (
            <div className="tx3cm-table-wrap">
              <table className="tx3cm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Card #</th>
                    <th>Facility code</th>
                    <th>Status</th>
                    <th className="tx3cm-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card) => {
                    const isEditing = editingId === card.CardID;
                    const busy = rowBusy === card.CardID;
                    if (isEditing) {
                      return (
                        <tr key={card.CardID} className="tx3cm-editing">
                          <td>
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                              aria-label="Name"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.cardNumber}
                              onChange={(e) => setEditForm((f) => ({ ...f, cardNumber: e.target.value }))}
                              aria-label="Card number"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.facilityCode}
                              onChange={(e) => setEditForm((f) => ({ ...f, facilityCode: e.target.value }))}
                              aria-label="Facility code"
                            />
                          </td>
                          <td><span className={`tx3cm-status ${card.Status === 1 ? 'on' : 'off'}`}>{statusLabel(card.Status)}</span></td>
                          <td className="tx3cm-actions-col">
                            <button
                              type="button"
                              className="tx3cm-btn primary sm"
                              onClick={() => saveEdit(card.CardID)}
                              disabled={busy || !running}
                              title={!running ? 'Start the serial service to save changes' : 'Save'}
                            >
                              {busy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="tx3cm-btn ghost sm"
                              onClick={cancelEdit}
                              disabled={busy}
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={card.CardID}>
                        <td>
                          <button
                            type="button"
                            className="tx3cm-namebtn"
                            onClick={() => setHistCard(card)}
                            title="View scan history"
                          >
                            {card.CardName || '(Unnamed)'}
                          </button>
                        </td>
                        <td className="tx3cm-mono">{card.CardNumber}</td>
                        <td className="tx3cm-mono">{card.FacilityCode}</td>
                        <td><span className={`tx3cm-status ${card.Status === 1 ? 'on' : 'off'}`}>{statusLabel(card.Status)}</span></td>
                        <td className="tx3cm-actions-col">
                          <button
                            type="button"
                            className="tx3cm-btn ghost sm"
                            onClick={() => beginEdit(card)}
                            disabled={busy || (editingId != null && editingId !== card.CardID)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="tx3cm-btn danger sm"
                            onClick={() => deleteCard(card)}
                            disabled={busy || !running}
                            title={!running ? 'Start the serial service to delete cards' : 'Delete'}
                          >
                            {busy ? 'Deleting…' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Scan-history modal */}
        {histCard && (
          <ScanHistoryModal
            title={histCard.CardName}
            cardNumber={histCard.CardNumber}
            onClose={() => setHistCard(null)}
          />
        )}
      </div>
    </ProtectedComponent>
  );
}

export default TX3CardManager;
